/**
 * Utility functions for image references in text (@mention syntax)
 */

/**
 * Normalizes Umlauts to ASCII equivalents and removes spaces
 * 
 * @param {string} text - Text with potential umlauts and spaces
 * @returns {string} - Normalized text without umlauts and without spaces
 */
export function normalizeUmlauts(text) {
  if (!text) return text
  
  // First, normalize Unicode characters (e.g., combine decomposed characters)
  // This handles cases where ä might be stored as 'a' + combining diaeresis (U+0308)
  let normalized = text.normalize('NFC')
  
  // Map of umlauts to ASCII equivalents
  const umlautMap = {
    'ä': 'ae',
    'ö': 'oe',
    'ü': 'ue',
    'Ä': 'Ae',
    'Ö': 'Oe',
    'Ü': 'Ue',
    'ß': 'ss',
    'é': 'e',
    'è': 'e',
    'ê': 'e',
    'ë': 'e',
    'É': 'E',
    'È': 'E',
    'Ê': 'E',
    'Ë': 'E',
    'á': 'a',
    'à': 'a',
    'â': 'a',
    'ã': 'a',
    'Á': 'A',
    'À': 'A',
    'Â': 'A',
    'Ã': 'A',
    'ó': 'o',
    'ò': 'o',
    'ô': 'o',
    'õ': 'o',
    'Ó': 'O',
    'Ò': 'O',
    'Ô': 'O',
    'Õ': 'O',
    'ú': 'u',
    'ù': 'u',
    'û': 'u',
    'Ú': 'U',
    'Ù': 'U',
    'Û': 'U',
    'í': 'i',
    'ì': 'i',
    'î': 'i',
    'Í': 'I',
    'Ì': 'I',
    'Î': 'I',
    'ç': 'c',
    'Ç': 'C',
    'ñ': 'n',
    'Ñ': 'N'
  }
  
  // Replace each umlaut/character with its ASCII equivalent
  let result = ''
  for (let i = 0; i < normalized.length; i++) {
    const char = normalized[i]
    // Skip spaces
    if (char === ' ' || char === '\t' || char === '\n' || char === '\r') {
      continue
    }
    // Check if character is in map or check for combining marks
    if (umlautMap[char]) {
      result += umlautMap[char]
    } else if (char.charCodeAt(0) === 0x0308) { // Combining diaeresis (¨)
      // Skip combining marks - they're handled by the base character
      continue
    } else if (char.charCodeAt(0) === 0x0300 || // Combining grave
               char.charCodeAt(0) === 0x0301 || // Combining acute
               char.charCodeAt(0) === 0x0302 || // Combining circumflex
               char.charCodeAt(0) === 0x0303 || // Combining tilde
               char.charCodeAt(0) === 0x0327 || // Combining cedilla
               char.charCodeAt(0) === 0x0304) { // Combining macron
      // Skip combining marks
      continue
    } else {
      result += char
    }
  }
  
  return result
}

/**
 * Extracts a base reference name from a filename
 * Removes timestamp prefixes and file extensions
 * Normalizes umlauts to ASCII equivalents (ä -> ae, ö -> oe, ü -> ue, ß -> ss)
 * Removes spaces from the filename
 * 
 * @param {string} filename - The original filename (e.g. "1698765432_abc123_Schritt1.png")
 * @returns {string} - Base name without extension, normalized (e.g. "Schritt1")
 */
export function getImageReferenceName(filename) {
  if (!filename) return 'image'
  
  // Remove file extension
  let nameWithoutExt = filename.replace(/\.[^/.]+$/, '')
  
  // Check if filename has timestamp/prefix pattern: "timestamp_uniqueid_filename"
  // Extract the last part after the last underscore
  const parts = nameWithoutExt.split('_')
  
  let baseName
  
  if (parts.length > 2) {
    // Check if first part looks like a timestamp (all digits, >= 10 chars)
    const firstPart = parts[0]
    if (/^\d+$/.test(firstPart) && firstPart.length >= 10) {
      // Likely has timestamp prefix, skip the first two parts (timestamp and uniqueId)
      baseName = parts.slice(2).join('_') || nameWithoutExt
    } else {
      // Not a timestamp, use the whole name
      baseName = nameWithoutExt
    }
  } else if (parts.length === 2) {
    // Might be timestamp_filename or just filename_filename
    // Check if first part looks like a timestamp (all digits or very long)
    const firstPart = parts[0]
    if (/^\d+$/.test(firstPart) && firstPart.length >= 10) {
      // Looks like a timestamp, use second part
      baseName = parts[1] || nameWithoutExt
    } else {
      // Otherwise use the whole name without extension
      baseName = nameWithoutExt
    }
  } else {
    // No prefix pattern, return name without extension
    baseName = nameWithoutExt
  }
  
  // Normalize umlauts to ASCII equivalents
  return normalizeUmlauts(baseName)
}

/**
 * Generates a unique reference name by checking against existing names
 * Adds a number suffix if the name already exists (case-insensitive)
 * 
 * @param {string} baseName - The base name to make unique
 * @param {Array<string>} existingNames - Array of existing reference names
 * @returns {string} - Unique reference name
 */
export function generateUniqueReferenceName(baseName, existingNames) {
  if (!baseName) baseName = 'image'
  
  // Normalize existing names to lowercase for comparison
  const existingLower = (existingNames || []).map(name => name.toLowerCase())
  const baseLower = baseName.toLowerCase()
  
  // Check if base name is already unique
  if (!existingLower.includes(baseLower)) {
    return baseName
  }
  
  // Find next available number
  let counter = 1
  let newName = `${baseName}${counter}`
  
  while (existingLower.includes(newName.toLowerCase())) {
    counter++
    newName = `${baseName}${counter}`
  }
  
  return newName
}

/**
 * Finds an image by its reference name (case-insensitive)
 * 
 * @param {Array} images - Array of image objects with referenceName property
 * @param {string} referenceName - The reference name to find
 * @returns {Object|null} - The matching image object or null
 */
export function findImageByReferenceName(images, referenceName) {
  if (!images || !referenceName) {
    return null
  }

  const normalizedSearch = referenceName.toLowerCase()

  const found = images.find(img => {
    // WICHTIG: referenceName hat Priorität - verwende immer referenceName wenn vorhanden
    // Nur wenn referenceName fehlt, generiere es aus filename (nicht name)
    const imgRefName = img.referenceName || getImageReferenceName(img.filename || img.name || '')
    return imgRefName.toLowerCase() === normalizedSearch
  }) || null

  return found
}

/**
 * Parses text and extracts image references (@mention syntax)
 * Returns an array of segments: { type: 'text' | 'mention', content: string, referenceName?: string }
 * 
 * @param {string} text - The text to parse
 * @returns {Array} - Array of segments
 */
export function parseImageReferences(text) {
  if (!text) return [{ type: 'text', content: '' }]
  
  const segments = []
  // Match @ followed by ASCII word characters, hyphens, and underscores
  // \w matches ASCII letters (a-z, A-Z), digits (0-9), and underscore
  const mentionRegex = /@(\w+[\w\-_]*)/g
  let lastIndex = 0
  let match
  
  while ((match = mentionRegex.exec(text)) !== null) {
    // WICHTIG: Ignoriere E-Mail-Adressen
    // Prüfe, ob vor dem @ ein Word-Character steht (dann ist es eine E-Mail)
    const charBeforeAt = match.index > 0 ? text[match.index - 1] : null
    const isEmail = charBeforeAt && /\w/.test(charBeforeAt)
    
    // Oder: Prüfe, ob nach dem Mention ein Punkt gefolgt von einem Word-Character kommt
    // (dann ist es wahrscheinlich eine E-Mail wie "name@domain.com")
    // ABER: Wenn nach dem Punkt ein Leerzeichen kommt, ist es KEINE E-Mail
    const textAfterMatch = text.substring(match.index + match[0].length)
    const hasDotAfter = /^\.\w/.test(textAfterMatch) // Punkt gefolgt von Buchstabe/Zahl
    
    if (isEmail || hasDotAfter) {
      // Es ist eine E-Mail-Adresse, nicht eine Mention - überspringe
      continue
    }
    
    // Add text before the mention
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        content: text.substring(lastIndex, match.index)
      })
    }
    
    // Add the mention (referenceName is already normalized at upload time)
    segments.push({
      type: 'mention',
      content: match[0], // Full match including @
      referenceName: match[1] // Just the name part (already ASCII)
    })
    
    lastIndex = match.index + match[0].length
  }
  
  // Add remaining text after last mention
  if (lastIndex < text.length) {
    segments.push({
      type: 'text',
      content: text.substring(lastIndex)
    })
  }
  
  // If no mentions found, return single text segment
  if (segments.length === 0) {
    return [{ type: 'text', content: text }]
  }
  
  return segments
}

/**
 * Validates if all image references in text exist in the images array
 * 
 * @param {string} text - The text to validate
 * @param {Array} images - Array of available images
 * @returns {Array} - Array of invalid reference names
 */
export function validateImageReferences(text, images) {
  if (!text || !images || images.length === 0) return []
  
  const segments = parseImageReferences(text)
  const mentions = segments.filter(s => s.type === 'mention')
  const invalidRefs = []
  
  for (const mention of mentions) {
    const image = findImageByReferenceName(images, mention.referenceName)
    if (!image) {
      invalidRefs.push(mention.referenceName)
    }
  }
  
  return invalidRefs
}

/**
 * Replaces all occurrences of an image reference name in text with a new reference name
 * Case-insensitive matching, uses word boundaries to avoid partial matches
 * 
 * @param {string} oldReferenceName - The old reference name to replace
 * @param {string} newReferenceName - The new reference name to use
 * @param {string} text - The text containing @mentions
 * @returns {string} - Text with replaced reference names
 */
export function replaceImageReferenceInText(oldReferenceName, newReferenceName, text) {
  if (!text || !oldReferenceName || !newReferenceName) return text
  
  // Escape special regex characters in the old reference name
  const escapedOldName = oldReferenceName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  
  // Use word boundary to match complete reference names only
  // Match @oldReferenceName followed by word boundary or end of string
  const regex = new RegExp(`@${escapedOldName}\\b`, 'gi')
  
  // Replace all occurrences
  return text.replace(regex, `@${newReferenceName}`)
}
